import { useEffect, useState } from "react";
import clsx from "clsx";
import { useInterviewRunner } from "../state/useInterviewRunner";
import { ChatPane } from "../components/chat/ChatPane";
import { FieldPane } from "../components/fields/FieldPane";
import { SafetyBanner } from "../components/safety/SafetyBanner";
import { GovernancePanel } from "../components/governance/GovernancePanel";
import { Button } from "../components/ui/Button";
import { selectIsPaused, selectProgress } from "../engine/selectors";
import { api, type HealthResponse } from "../api/client";
import { t } from "../i18n/strings";
import type { Locale } from "../templates/types";

type MobilePane = "conversation" | "data";

/**
 * §17: "<1024px: stacked, with a segmented control (Conversation | Data n/7)
 * whose badge updates live as fields fill." Mirrors the header Tabs' visual
 * pattern (role="tablist"/"tab", no separate tabpanel wiring — same level of
 * ARIA completeness as that component) but is local to this view since it
 * only ever controls the two panes below. Hidden entirely at `lg:` and up,
 * where the two-pane side-by-side layout is unconditional.
 */
function MobilePaneSwitch({
  pane,
  onChange,
  filled,
  total,
  locale,
}: {
  pane: MobilePane;
  onChange: (pane: MobilePane) => void;
  filled: number;
  total: number;
  locale: Locale;
}) {
  return (
    <div
      role="tablist"
      aria-label={t(locale, "runCheckIn.mobilePane.ariaLabel")}
      className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-paper-dim p-1 lg:hidden"
    >
      {[
        { id: "conversation" as const, label: t(locale, "runCheckIn.mobilePane.conversation") },
        { id: "data" as const, label: t(locale, "runCheckIn.mobilePane.data", { filled, total }) },
      ].map((item) => {
        const active = pane === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={clsx(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              active ? "bg-paper text-ink shadow-sm" : "text-ink-soft hover:text-ink",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function RunCheckIn() {
  const { state, dispatch, sendMessage, startInterview, finalizeFollowup, reset } = useInterviewRunner();
  const isPaused = selectIsPaused(state);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [mobilePane, setMobilePane] = useState<MobilePane>("conversation");
  const { filled, total } = selectProgress(state);

  useEffect(() => {
    const controller = new AbortController();
    api
      .health(controller.signal)
      .then(setHealth)
      .catch(() => setHealth(null));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (state.phase === "complete") {
      finalizeFollowup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  if (state.phase === "idle") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="font-display text-2xl text-ink">{t(state.locale, "start.heading")}</h1>
        <p className="max-w-md text-sm text-ink-soft">{t(state.locale, "start.body")}</p>
        <Button onClick={startInterview}>{t(state.locale, "start.button")}</Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3">
      {state.phase === "complete" && (
        // A one-time state transition worth announcing — role="status" here
        // is not a duplicate of ProgressMeter's aria-live (that tracks the
        // running field count; this fires once, on completion).
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-sage/40 bg-sage-dim p-3 text-sm text-sage-strong"
        >
          {t(state.locale, "complete.banner")}
          <Button variant="ghost" className="ml-3" onClick={reset}>
            {t(state.locale, "complete.button")}
          </Button>
        </div>
      )}
      <MobilePaneSwitch pane={mobilePane} onChange={setMobilePane} filled={filled} total={total} locale={state.locale} />
      {/*
        ≥1024px (`lg:`): unchanged side-by-side two-pane grid, both panes
        always visible. <1024px: exactly one of the two wrapper divs is
        shown at a time, driven by the segmented control above — the
        `lg:flex`/`lg:block` overrides win at the breakpoint regardless of
        `mobilePane`, so desktop behavior never depends on this state.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
        <div
          data-testid="mobile-conversation-pane"
          className={clsx("min-h-0", mobilePane === "conversation" ? "flex flex-col" : "hidden", "lg:flex lg:flex-col")}
        >
          <ChatPane
            turns={state.turns}
            phase={state.phase}
            onSend={sendMessage}
            locale={state.locale}
            banner={
              isPaused ? (
                <SafetyBanner
                  safety={state.safety}
                  locale={state.locale}
                  onEnd={() => {}}
                  onRestart={reset}
                />
              ) : undefined
            }
          />
        </div>
        <div
          data-testid="mobile-data-pane"
          className={clsx("min-h-0", mobilePane === "data" ? "block" : "hidden", "lg:block")}
        >
          <FieldPane state={state} />
        </div>
      </div>
      <GovernancePanel
        state={state}
        health={health}
        onSafetyPause={(paused) =>
          dispatch({
            type: "SUMMARY_PAUSED",
            safety: paused.safety,
            followup: paused.followup,
            continuityToken: paused.continuity_token,
            at: new Date().toISOString(),
          })
        }
      />
    </div>
  );
}
